import { isNotNull } from 'common/TypeUtilities';
import { orderBy } from 'lodash';
import { useMemo } from 'react';
import { AddOnItemType } from 'src/gqlReactTypings.generated.d';
import { useDispatch } from 'src/store';
import {
  decAddOnItemQuantity,
  decLineItemQuantity,
  incAddOnItemQuantity,
  incLineItemQuantity,
} from 'src/store/cart/actions';
import { CartAddOnItem, ServerAddOnItem } from 'src/store/cart/types';
import { isAddOnItem, UpdatableItemType } from './ConsumerOrderCartInfo';

interface UseVisibleAddOnItemsParams {
  availableAddOnItems: ServerAddOnItem[];
  addOnItems: CartAddOnItem[];
  setAddOnQuantity?: (params: { addOnItem: ServerAddOnItem; quantity: number; shefId: string }) => void;
}

export function useVisibleAddOnItems({
  availableAddOnItems,
  addOnItems,
  setAddOnQuantity,
}: UseVisibleAddOnItemsParams): Array<[ServerAddOnItem, number]> {
  const visibleAddons = useMemo(() => {
    const sorted = orderBy(
      availableAddOnItems,
      (item) => [AddOnItemType.Side, AddOnItemType.Donation].indexOf(item.addonType),
      'asc'
    );
    return sorted
      .map((item) => {
        const selectedQuantity = addOnItems.find((i) => i.id === item.id)?.quantity || 0;

        // Only show add on items if either is true:
        // a. User can currently modify them in cart (increment / decrement)
        // b. Add on item is already in cart (quantity > 0)

        const isDonation = item.addonType === AddOnItemType.Donation;
        const showAddOnItem = (isDonation && !!setAddOnQuantity) || selectedQuantity > 0;
        const finalItem: [ServerAddOnItem, number] | null = showAddOnItem ? [item, selectedQuantity] : null;
        return finalItem;
      })
      .filter(isNotNull);
  }, [addOnItems, availableAddOnItems, setAddOnQuantity]);

  return visibleAddons;
}

export function useItemQuantifyModifiers({
  isEditingOrder,
  deliveryDate,
}: {
  isEditingOrder: boolean;
  deliveryDate: string;
}) {
  const dispatch = useDispatch();

  const increment = (item: UpdatableItemType) => {
    dispatch(
      isAddOnItem(item)
        ? incAddOnItemQuantity(isEditingOrder, { addOnItem: item })
        : incLineItemQuantity(isEditingOrder, { foodItem: item.foodItem, location: 'consumer-order', deliveryDate })
    );
  };

  const decrement = (item: UpdatableItemType) => {
    dispatch(
      isAddOnItem(item)
        ? decAddOnItemQuantity(isEditingOrder, { addOnItem: item })
        : decLineItemQuantity(isEditingOrder, { foodItem: item.foodItem, deliveryDate, location: 'consumer-order' })
    );
  };

  return { increment, decrement };
}
